/*
 * "A sales claim changed" signal, for the list to refetch. The form usually
 * runs in another tab (the list opens tasks with target="_blank"), so the same-
 * tab window event alone never reaches the list: a BroadcastChannel carries it
 * across tabs, falling back to a localStorage `storage` event where the channel
 * is unavailable. A channel never delivers to the instance that posted, so the
 * window event still covers a list in the same tab.
 */

import { SALES_CLAIM_CHANGED_EVENT } from './instant_alert_config';

export const SALES_CLAIM_CHANNEL = 'sales-task-claims';
export const SALES_CLAIM_STORAGE_KEY = 'sales_task_claim_changed';

export interface ClaimChangedDetail {
  task_uuid: string;
}

let channel: BroadcastChannel | null | undefined;

// undefined = not tried yet; null = unavailable in this browser.
const get_channel = (): BroadcastChannel | null => {
  if (channel !== undefined) return channel;
  try {
    channel =
      typeof BroadcastChannel === 'function' ? new BroadcastChannel(SALES_CLAIM_CHANNEL) : null;
  } catch {
    channel = null;
  }
  return channel;
};

export const reset_claim_broadcast = (): void => {
  if (channel) channel.close();
  channel = undefined;
};

export const publish_claim_changed = (task_uuid: string): void => {
  const detail: ClaimChangedDetail = { task_uuid };
  window.dispatchEvent(new CustomEvent(SALES_CLAIM_CHANGED_EVENT, { detail }));
  const ch = get_channel();
  if (ch) {
    try {
      ch.postMessage(detail);
      return;
    } catch {
      // fall through to storage
    }
  }
  try {
    localStorage.setItem(SALES_CLAIM_STORAGE_KEY, JSON.stringify({ ...detail, at: Date.now() }));
  } catch {
    // Private mode: other tabs catch up on focus.
  }
};

// Returns the unsubscribe.
export const subscribe_claim_changed = (listener: () => void): (() => void) => {
  const on_storage = (e: StorageEvent) => {
    if (e.key === SALES_CLAIM_STORAGE_KEY) listener();
  };
  window.addEventListener(SALES_CLAIM_CHANGED_EVENT, listener);
  window.addEventListener('storage', on_storage);
  const ch = get_channel();
  const on_message = () => listener();
  if (ch) ch.addEventListener('message', on_message);
  return () => {
    window.removeEventListener(SALES_CLAIM_CHANGED_EVENT, listener);
    window.removeEventListener('storage', on_storage);
    if (ch) ch.removeEventListener('message', on_message);
  };
};
